// EphMail.jsx

import React, { useState, useEffect, useContext, useRef } from "react";
import { UrlContext } from "../contexts/UrlContext";
import Swal from "sweetalert2";
import { DateTime } from "luxon";
import NewEmail from "../components/EphEmail/NewEmail";
import { AnimatePresence } from "framer-motion";

export default function EphMail () {
  useEffect(() => {
    document.title = "EPH Email";
  }, []);
  const { API_URL } = useContext(UrlContext);
  const [selectedBox, setSelectedBox] = useState("Inbox");
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [openDropdownIndex, setOpenDropdownIndex] = useState(null);
  const [composeEmail, setComposeEmail] = useState(null);
  const tokenObj = localStorage.getItem("eph-token");
  const userEmail = tokenObj ? JSON.parse(tokenObj).email : "";
  const dropdownRefs = useRef([]);
  const [mailboxData, setMailboxData] = useState({
    Inbox: [],
    Sent: [],
    Drafts: [],
    "Deleted Items": [],
  });

  const renderRotatedButton = (label) => (
    <button
      key={label}
      onClick={() => {
        setSelectedBox(label);
        setSelectedEmail(null);
      }}
      style={{
        writingMode: "vertical-rl",
        transform: "rotate(180deg)",
        backgroundColor: selectedBox === label ? "#365561" : "inherit",
        color: "white",
        padding: "10px 5px",
        border: "none",
        fontWeight: "bold",
        cursor: "pointer",
        width: "100%", // Ensures it spans properly inside the flex column
        outline: "none",
      }}
    >
      {label}
    </button>
  );

  const emails = mailboxData[selectedBox] || [];
  const formatDate = (dateString) => {
    const options = {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    };
    return new Date(dateString).toLocaleString(undefined, options);
  };

  const getEmails = async () => {
    const authToken = localStorage.getItem("accesstoken");
    try {
      const response = await fetch(`${API_URL}get-user-email-via-graph?accessToken=${authToken}`);
  
      if (response.status === 200) {
        const data = await response.json();
        if (data.status === "success") {
          setMailboxData(prev => ({
            ...prev,
            Inbox: data.emailData || [],
          }));
        } else {
          Swal.fire("Error", data.message || "Failed to fetch email!", "error");
        }
      } else {
        const errorData = await response.json();
        Swal.fire("Error", errorData.text || "Failed to fetch email!", "error");
      }
    } catch (error) {
      Swal.fire("Error", "Network error while fetching email!", "error");
    }
  };

  const getSentEmails = async () => {
    const authToken = localStorage.getItem("accesstoken");
    try {
      const response = await fetch(`${API_URL}get-user-sent-emails-via-graph?accessToken=${authToken}`);
  
      if (response.status === 200) {
        const data = await response.json();
        if (data.status === "success") {
          setMailboxData(prev => ({
            ...prev,
            Sent: data.emailData || [],
          }));
        } else {
          Swal.fire("Error", data.message || "Failed to fetch email!", "error");
        }
      } else {
        const errorData = await response.json();
        Swal.fire("Error", errorData.text || "Failed to fetch email!", "error");
      }
    } catch (error) {
      Swal.fire("Error", "Network error while fetching email!", "error");
    }
  };

  useEffect(() => {
    getEmails();
    getSentEmails();
  }
  , []);

  const sendEmailViaGraph = async (emailData) => {
    const accessToken = localStorage.getItem("accesstoken");
    try {
      const response = await fetch(`${API_URL}send-eph-email-via-graph`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          accessToken,
          to: emailData.to || [],
          cc_email: emailData.cc_email || [],
          bcc_email: emailData.bcc_email || [],
          subject: emailData.subject || "",
          body: emailData.body || "",
          attachment: emailData.attachment || [],
        }),
      });
  
      if (response.status === 200) {
        const result = await response.json();
        if (result.status === "success") {
          Swal.fire("Success", "Email sent successfully!", "success");
        } else {
          Swal.fire("Error", result.text || "Failed to send email!", "error");
        }
      } else {
        const errorData = await response.json();
        Swal.fire("Error", errorData.text || "Failed to send email!", "error");
      }
    } catch (error) {
      Swal.fire("Error", "Network error while sending email!", "error");
    }
  };  

  useEffect(() => {
    if (selectedEmail && selectedEmail.hasAttachments && !selectedEmail.attachments) {
      const fetchAttachments = async () => {
        const authToken = localStorage.getItem("accesstoken");
        try {
          const response = await fetch(`${API_URL}get-email-attachments-via-graph?accessToken=${authToken}&messageID=${selectedEmail.id}`);
          if (response.status === 200) {
            const data = await response.json();
            if (data.status === "success") {
              setSelectedEmail(prev => ({ ...prev, attachments: data.attachments }));
            } else {
              Swal.fire("Error", data.message || "Failed to fetch attachments!", "error");
            }
          } else {
            const errorData = await response.json();
            Swal.fire("Error", errorData.text || "Failed to fetch attachments!", "error");
          }
        } catch (error) {
          Swal.fire("Error", "Network error while fetching attachments!", "error");
        }
      };
      fetchAttachments();
    }
  }, [selectedEmail?.id]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        dropdownRefs.current.every(
          (ref) => ref && !ref.contains(event.target)
        )
      ) {
        setOpenDropdownIndex(null);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  function parseICS(content) {
    try {
      const text = typeof content === "string" ? content : atob(content); // decode base64 if needed
      const lines = text.split("\n");
  
      const summary = lines.find((l) => l.startsWith("SUMMARY:"))?.split(":")[1];
      const dtStart = lines.find((l) => l.startsWith("DTSTART:"))?.split(":")[1];
      const dtEnd = lines.find((l) => l.startsWith("DTEND:"))?.split(":")[1];
      const location = lines.find((l) => l.startsWith("LOCATION:"))?.split(":")[1];
      const organizer = lines.find((l) => l.startsWith("ORGANIZER;"))?.split(":")[1];
      const attendees = lines
        .filter((l) => l.startsWith("ATTENDEE;"))
        .map((l) => l.split(":")[1]?.replace("mailto:", ""));
  
      return {
        summary,
        dtStart,
        dtEnd,
        location,
        organizer,
        attendees,
      };
    } catch (err) {
      return {};
    }
  }
  
  function formatICSDate(dtStr) {
    if (!dtStr) return "Unknown time";
    const date = new Date(dtStr);
    return date.toLocaleString();
  }
  
  const getMeetingPlatform = (content) => {
    if (/teams\.microsoft\.com/i.test(content)) return { name: "Microsoft Teams", icon: "fa-brands fa-microsoft" };
    if (/zoom\.us|zoom\.com/i.test(content)) return { name: "Zoom", icon: "fa-brands fa-zoom" };
    if (/meet\.google\.com/i.test(content)) return { name: "Google Meet", icon: "fa-brands fa-google" };
    if (/webex\.com/i.test(content)) return { name: "Webex", icon: "fa-solid fa-video" };
    return { name: "Online", icon: "fa-solid fa-link" };
  }

  // Converts Microsoft Graph time + zone to local
  const formatGraphTime = (dateTimeStr, timeZoneStr = "UTC") => {
    try {
      const dt = DateTime.fromISO(dateTimeStr, { zone: timeZoneStr });
      return dt.toLocal().toLocaleString(DateTime.DATETIME_MED);
    } catch {
      return "Invalid date";
    }
  };

  const handleReply = () => {
    if (!selectedEmail) return;
  
    const to = [selectedEmail.from?.emailAddress?.address];
    const subject = selectedEmail.subject?.startsWith("Re:") ? selectedEmail.subject : `Re: ${selectedEmail.subject}`;
  
    setComposeEmail({
      mode: "reply",
      to,
      subject,
      quotedEmail: selectedEmail,
    });
  };
  
  const handleReplyAll = () => {
    if (!selectedEmail) return;
  
    const sender = selectedEmail.from?.emailAddress?.address;
    const recipients = [
      ...new Set([
        ...selectedEmail.toRecipients.map(r => r.emailAddress.address),
        ...selectedEmail.ccRecipients.map(r => r.emailAddress.address),
      ]),
    ].filter(email => email !== userEmail && email !== sender); // exclude self and sender
  
    const subject = selectedEmail.subject?.startsWith("Re:") ? selectedEmail.subject : `Re: ${selectedEmail.subject}`;
    const to = [sender, ...recipients];
  
    setComposeEmail({
      mode: "replyAll",
      to,
      cc: selectedEmail.ccRecipients.map(r => r.emailAddress.address),
      subject,
      quotedEmail: selectedEmail,
    });
  };
  
  const handleForward = () => {
    if (!selectedEmail) return;
  
    const subject = selectedEmail.subject?.startsWith("Fwd:") ? selectedEmail.subject : `Fwd: ${selectedEmail.subject}`;
  
    setComposeEmail({
      mode: "forward",
      subject,
      quotedEmail: selectedEmail,
    });
  };

  console.log(selectedEmail);

  return (

    <div className="bg-gray-500 flex flex-col h-screen">
      {/* Top Nav */}
      <div className="bg-gray-700 text-white p-2 flex gap-4 items-center">
        <button
          className="bg-gray-700 hover:bg-gray-600 text-white px-3 py-1 rounded"
          onClick={() =>
            setComposeEmail({
              to: [],
              cc: [],
              subject: "",
              quotedEmail: null,
            })
          }
        >
          New Mail
        </button>
        <button className="bg-gray-700 hover:bg-gray-600 text-white px-3 py-1 rounded">Reply</button>
        <button className="bg-gray-700 hover:bg-gray-600 text-white px-3 py-1 rounded">Reply All</button>
        <button className="bg-gray-700 hover:bg-gray-600 text-white px-3 py-1 rounded">Forward</button>
      </div>

      <div  className="flex flex-grow overflow-hidden mt-2">
        {/* Left - Vertical Nav */}
        <div
          className="w-[40px] bg-gray-700 flex flex-col items-center justify-start py-4"
          style={{ overflowY: "auto" }}
        >
          {["Inbox", "Sent", "Drafts", "Deleted Items"].map(renderRotatedButton)}
        </div>

        {/* Middle - Email List */}
        <div className="w-[18%] bg-gray-100 p-2 overflow-y-auto h-full">
          {emails.map((email, idx) => {
            const isSelected = selectedEmail?.id === email.id;
            const isRead = email.isRead;

            return (
              <div
                key={idx}
                onClick={() => setSelectedEmail(email)}
                className={`cursor-pointer p-2 mb-2 rounded shadow ${
                  isSelected ? "bg-[#365561] text-white" : "bg-white hover:bg-gray-200"
                }`}
              >
                {/* Top row: Date & Status Icon */}
                <div className="flex justify-between items-center text-xs">
                  <span>{formatDate(email.receivedDateTime)}</span>
                  <span className="relative group ml-2">
                    <i className={`fa-solid ${isRead ? "fa-envelope-open-text" : "fa-envelope"}`} />
                  </span>
                </div>

                {/* From Name */}
                <h3 className="text-sm font-medium">{email.from?.emailAddress?.name}</h3>

                {/* Subject with optional attachment icon */}
                <div className="flex items-center justify-between text-sm">
                  <span>{email.subject}</span>
                  {email.hasAttachments && (
                    <span className="relative group text-gray-500 ml-2">
                      <i className="fa-solid fa-paperclip" />
                    </span>
                  )}
                </div>

                {/* Email preview */}
                <div className="text-xs">
                  {email.bodyPreview?.substring(0, 40)}...
                </div>
              </div>
            );
          })}

          {emails.length === 0 && (
            <div className="text-sm text-gray-500 p-2">No emails</div>
          )}
        </div>

        {/* Right - Email Details */}
        <div className="w-[80%] bg-white p-4 overflow-y-auto shadow-lg">
          {selectedEmail ? (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">{selectedEmail.subject}</h3>
              <div className="flex items-start justify-between flex-wrap gap-4">
                {/* Left - Sender Info */}
                <div className="flex items-start gap-3 min-w-[200px] max-w-[75%]">
                  {/* Avatar (First Letter) */}
                  <div className="w-10 h-10 flex-shrink-0 flex-grow-0 bg-gray-300 text-black flex items-center justify-center rounded-full text-lg font-semibold">
                    {selectedEmail.from?.emailAddress?.name?.charAt(0) || "?"}
                  </div>
                  <div className="min-w-0 break-words">
                    <div className="text-sm font-medium">{selectedEmail.from?.emailAddress?.name}</div>
                    <div className="text-xs text-gray-500">{selectedEmail.from?.emailAddress?.address}</div>
                    <div className="text-xs text-gray-700">
                      <span className="font-medium">To: </span>
                      {selectedEmail.toRecipients?.map((recipient, idx) => (
                        <span key={idx} className="relative group">
                          <span className="hover:underline cursor-pointer px-1">
                            {recipient.emailAddress.name}
                          </span>
                          {/* Tooltip on hover */}
                          <div className="pointer-events-none absolute bg-gray-800 text-white text-xs rounded px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 left-1/2 transform -translate-x-1/2 mt-1 z-10 whitespace-nowrap">
                            {recipient.emailAddress.address}
                          </div>
                          {idx < selectedEmail.toRecipients.length - 1 && ", "}
                        </span>
                      ))}
                    </div>
                    {selectedEmail.ccRecipients?.length > 0 && (
                      <div className="text-xs text-gray-700 mt-1">
                        <span className="font-medium">CC: </span>
                        {selectedEmail.ccRecipients.map((recipient, idx) => (
                          <span key={idx} className="relative group">
                            <span className="hover:underline cursor-pointer px-1">
                              {recipient.emailAddress.name}
                            </span>
                            {/* Tooltip on hover */}
                            <div className="pointer-events-none absolute bg-gray-800 text-white text-xs rounded px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 left-1/2 transform -translate-x-1/2 mt-1 z-10 whitespace-nowrap">
                              {recipient.emailAddress.address}
                            </div>
                            {idx < selectedEmail.toRecipients.length - 1 && ", "}
                          </span>
                        ))}
                      </div>
                    )}
                    {selectedEmail.hasAttachments && selectedEmail.attachments?.length > 0 && (
                      <div className="mt-3">
                        <div className="text-xs font-medium text-gray-700 mb-1">Attachments:</div>
                        <div className="flex flex-wrap gap-3">
                          {selectedEmail.attachments.map((att, idx) => {
                            const isCalendarInvite = att.contentType === "text/calendar" || att.name.endsWith(".ics");
                            const blobUrl = att.contentBytes
                              ? URL.createObjectURL(
                                  new Blob([Uint8Array.from(atob(att.contentBytes), c => c.charCodeAt(0))], {
                                    type: att.contentType,
                                  })
                                )
                              : "#";
                            if (isCalendarInvite) {
                              return (
                                <div key={idx} className="border border-blue-300 bg-blue-50 p-4 rounded-lg text-sm space-y-2 shadow-sm">
                                  {/* Calendar Invite Card */}
                                  <div className="flex items-center justify-between">
                                    <h4 className="text-blue-800 font-semibold">{parseICS(att.content)?.summary || "Meeting Invitation"}</h4>
                                    <span className="text-xs text-gray-500">{Math.round(att.size / 1024)} KB</span>
                                  </div>
                                  <div className="text-gray-700 space-y-1">
                                    <div><strong>When:</strong> {formatICSDate(att.content)}</div>
                                    {parseICS(att.content)?.location && (
                                      <div><strong>Where:</strong> {parseICS(att.content).location}</div>
                                    )}
                                    <div><strong>Organizer:</strong> {parseICS(att.content)?.organizer}</div>
                                    <div><strong>Attendees:</strong> {parseICS(att.content)?.attendees?.join(", ")}</div>
                                  </div>
                            
                                  <div className="flex gap-2 mt-2">
                                    <button className="bg-blue-600 text-white text-xs px-3 py-1 rounded hover:bg-blue-700">Accept</button>
                                    <button className="bg-gray-300 text-xs px-3 py-1 rounded hover:bg-gray-400">Tentative</button>
                                    <button className="bg-gray-300 text-xs px-3 py-1 rounded hover:bg-gray-400">Decline</button>
                                  </div>
                          
                                  <a
                                    href={URL.createObjectURL(new Blob([att.content], { type: att.contentType }))}
                                    download={att.name}
                                    className="text-blue-700 text-xs underline mt-1 inline-block"
                                  >
                                    View full invitation
                                  </a>
                                </div>
                              );
                            } else {
                              return (
                                <div
                                  key={idx}
                                  ref={(el) => (dropdownRefs.current[idx] = el)}
                                  className="relative border rounded-md p-2 bg-gray-50 cursor-pointer hover:shadow-md transition"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setOpenDropdownIndex(openDropdownIndex === idx ? null : idx);
                                  }}
                                >
                                  {/* Attachment Content Preview */}
                                  <div className="flex items-center gap-3">
                                    {/* File preview or icon */}
                                    {att.contentType?.startsWith("image/") ? (
                                      <img
                                        src={blobUrl}
                                        alt={att.name}
                                        className="w-14 h-14 object-cover rounded"
                                      />
                                    ) : (
                                      <div className="w-14 h-14 flex items-center justify-center bg-gray-200 rounded text-gray-600 text-xl">
                                        <i className="fa-solid fa-file"></i>
                                      </div>
                                    )}

                                    {/* File Name + Info */}
                                    <div className="flex-1 min-w-0">
                                      <div className="text-sm font-medium text-gray-800 truncate">
                                        {att.name}
                                      </div>
                                      <div className="text-xs text-gray-500">
                                        {Math.round(att.size / 1024)} KB • {att.contentType?.split("/")[1]}
                                      </div>
                                    </div>
                                  </div>

                                  {/* Dropdown on Click */}
                                  {openDropdownIndex === idx && (
                                    <div className="absolute right-0 top-full mt-2 bg-white border border-gray-300 rounded shadow-md text-xs z-30 w-48">
                                      <ul className="py-1 text-gray-800">
                                        {att.contentType?.startsWith("image/") && (
                                          <li className="px-3 py-1 hover:bg-gray-100 cursor-pointer">
                                            <a href={blobUrl} target="_blank" rel="noopener noreferrer">
                                              Preview
                                            </a>
                                          </li>
                                        )}
                                        <li className="px-3 py-1 hover:bg-gray-100 cursor-pointer">
                                          <a href={blobUrl} download={att.name}>
                                            Open
                                          </a>
                                        </li>
                                        <li className="px-3 py-1 hover:bg-gray-100 cursor-pointer">
                                          <a href={blobUrl} download={att.name}>
                                            Save As
                                          </a>
                                        </li>
                                        <li className="px-3 py-1 hover:bg-gray-100 cursor-pointer">
                                          Save All Attachments
                                        </li>
                                        <li className="px-3 py-1 hover:bg-gray-100 text-red-600 cursor-pointer">
                                          Remove Attachment
                                        </li>
                                        <li className="px-3 py-1 hover:bg-gray-100 cursor-pointer">Copy</li>
                                        <li className="px-3 py-1 hover:bg-gray-100 cursor-pointer">Select All</li>
                                      </ul>
                                    </div>
                                  )}
                                </div>
                              );
                            }
                          })}
                        </div>
                      </div>
                    )}
                    {/* Inline Meeting Info (only if no .ics but it's a meeting) */}
                    {!selectedEmail.hasAttachments && selectedEmail.meetingMessageType === "meetingRequest" && selectedEmail.startDateTime?.dateTime && (
                      <div className="relative border border-blue-200 rounded-lg bg-blue-50 shadow-sm mt-4 overflow-hidden">
                        <div className="absolute left-0 top-0 bottom-0 w-1 bg-blue-500" />
                        <div className="pl-4 pr-4 py-3 space-y-2 text-sm">
                          <div className="flex justify-between items-start w-full">
                            <h4 className="text-blue-800 font-semibold text-base pr-4 break-words">
                              📅 {selectedEmail.subject || "Meeting Invitation"}
                            </h4>
                            {(() => {
                              const { name, icon } = getMeetingPlatform(selectedEmail.body?.content || "");
                              return (
                                <span className="text-xs text-gray-500 flex items-center gap-1 whitespace-nowrap ml-2">
                                  <i className={`${icon}`} /> {name}
                                </span>
                              );
                            })()}
                          </div>
                          <hr className="border-blue-200" />
                          <div className="text-gray-700 space-y-1">
                            <div>
                              <strong>Organizer:</strong> {selectedEmail.from?.emailAddress?.name}
                            </div>
                            <div>
                              <strong>Time:</strong>{" "}
                              {formatGraphTime(selectedEmail.startDateTime?.dateTime, selectedEmail.startDateTime?.timeZone)} –{" "}
                              {formatGraphTime(selectedEmail.endDateTime?.dateTime, selectedEmail.endDateTime?.timeZone)}
                            </div>
                            {selectedEmail.toRecipients?.length > 0 && (
                              <div>
                                <strong>To:</strong>{" "}
                                {selectedEmail.toRecipients
                                  .map((r) => r.emailAddress?.name)
                                  .slice(0, 4)
                                  .join(", ")}
                                {selectedEmail.toRecipients.length > 4 && (
                                  <span className="text-blue-600"> +{selectedEmail.toRecipients.length - 4} others</span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Right - Action Buttons */}
                <div className="flex flex-col items-end  min-w-[180px] max-w-[25%]">
                  <div className="flex gap-3">
                    <button
                      className="bg-gray-700 hover:bg-gray-600 text-white px-3 py-1 rounded"
                      onClick={handleReply}
                      disabled={!selectedEmail}
                    >
                      Reply
                    </button>
                    <button
                      className="bg-gray-700 hover:bg-gray-600 text-white px-3 py-1 rounded"
                      onClick={handleReplyAll}
                      disabled={!selectedEmail}
                    >
                      Reply All
                    </button>
                    <button
                      className="bg-gray-700 hover:bg-gray-600 text-white px-3 py-1 rounded"
                      onClick={handleForward}
                      disabled={!selectedEmail}
                    >
                      Forward
                    </button>
                  </div>
                  {/* Date & Time Below Buttons */}
                  <div className="text-xs text-gray-500 mt-1">
                    {formatDate(selectedEmail.receivedDateTime)}
                  </div>
                </div>
              </div>

              <hr className="border-gray-300" />

              {/* Email Body */}
              <div
                className="mt-4 prose max-w-none text-gray-700"
                dangerouslySetInnerHTML={{ __html: selectedEmail.body?.content }}
              />
            </div>
          ) : (
            <div className="text-gray-400 text-center mt-20">Select an email to read</div>
          )}
        </div>
      </div>

      <AnimatePresence mode="wait">
        {composeEmail && (
          <NewEmail
            key="newEmail"
            onClose={() => setComposeEmail(null)}
            onSend={(data) => {
              console.log("Sending email:", data);
              sendEmailViaGraph(data);
              setComposeEmail(null);
            }}
            defaultTo={composeEmail.to || []}
            defaultCc={composeEmail.cc || []}
            defaultSubject={composeEmail.subject || ""}
            quotedEmail={composeEmail.quotedEmail || null}
          />
        )}
      </AnimatePresence>
    </div>
  );
};